// src/views/WeeklyChallengeHubView.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Lock,
  Play,
  RotateCcw,
  Trophy,
  Zap,
  Sparkles,
  ChevronRight
} from 'lucide-react';
import Mascot from '../components/Mascot';
import {
  getWeeklyChallengeProgress,
  clearWeeklyChallengeProgress
} from '../utils/weeklyChallengeStorage';

export default function WeeklyChallengeHubView({
  challenge,
  setView,
  userProgress,
  onStartSection,
  onExit,
  onCompleteChallenge
}) {
  const [progressData, setProgressData] = useState(() =>
    getWeeklyChallengeProgress(challenge?.id)
  );

  const activeSkin = userProgress?.currentSkin || 'bribark';

  // Bloquear el scroll de fondo mientras el modal esté montado
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Recargar progreso local al montar
  useEffect(() => {
    if (challenge?.id) {
      setProgressData(getWeeklyChallengeProgress(challenge.id));
    }
  }, [challenge?.id]);

  const sections = challenge?.sections || [];
  const totalSections = sections.length;

  // Secciones completadas
  const completedSections = useMemo(() => {
    return sections.filter(
      (sec) => progressData.sections?.[sec.id]?.status === 'completed'
    );
  }, [sections, progressData]);

  const completedCount = completedSections.length;
  const isAllCompleted = totalSections > 0 && completedCount >= totalSections;
  const progressPct = totalSections > 0 ? Math.round((completedCount / totalSections) * 100) : 0;

  // Regla de acceso:
  // - La sección 0 siempre está abierta.
  // - Una sección N está abierta si la sección N-1 está completada,
  //   o si la sección N ya tiene estado 'completed' o 'in_progress'.
  const isSectionUnlocked = (index) => {
    if (index === 0) return true;
    const prevSection = sections[index - 1];
    const prevStatus = progressData.sections?.[prevSection.id]?.status;
    const currentStatus = progressData.sections?.[sections[index].id]?.status;
    return prevStatus === 'completed' || currentStatus === 'completed' || currentStatus === 'in_progress';
  };

  const handleRestartChallenge = () => {
    if (window.confirm('¿Quieres reiniciar el progreso de este desafío semanal para volver a practicarlo desde cero?')) {
      clearWeeklyChallengeProgress(challenge.id);
      setProgressData({ challengeId: challenge.id, sections: {} });
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-0 sm:p-3 animate-in fade-in duration-300 select-none"
      style={{ overscrollBehavior: 'contain' }}
    >
      <div className="w-full max-w-lg h-full sm:h-[94vh] sm:max-h-[850px] sm:rounded-[2.5rem] bg-slate-100 flex flex-col overflow-hidden shadow-2xl relative border-0 sm:border sm:border-slate-200">
        
        {/* ── HEADER DEL MODAL ── */}
        <header className="shrink-0 px-4 pt-3.5 pb-2.5 bg-white border-b border-slate-200/80 flex items-center gap-3">
          <button
            onClick={onExit || (() => setView('lobby'))}
            className="p-2 rounded-xl bg-slate-100 border border-slate-200 text-slate-500 hover:text-indigo-600 hover:bg-slate-200 transition-all shadow-sm active:scale-90 shrink-0 cursor-pointer"
            aria-label="Cerrar desafío semanal"
          >
            <X size={18} strokeWidth={2.5} />
          </button>

          <div className="flex-1 min-w-0">
            <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block leading-none">
              Desafío de la Semana
            </span>
            <span className="text-sm font-black text-slate-800 truncate block mt-0.5">
              {challenge?.subtitle || challenge?.title}
            </span>
          </div>

          <div className="flex items-center gap-1.5 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200 text-amber-700 shrink-0">
            <Zap size={13} className="fill-amber-500 text-amber-500" />
            <span className="text-xs font-black">+{challenge?.xpReward || 350} XP</span>
          </div>
        </header>

        {/* ── CUERPO DEL MODAL (SCROLLABLE INTERNO) ── */}
        <main className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-4">
          
          {/* ── BANNER DESTACADO ── */}
          <div className="relative rounded-[2rem] overflow-hidden shadow-xl border-b-6 border-indigo-900 bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800 p-5 text-white space-y-3.5">
            {/* Glows decorativos */}
            <div className="absolute top-0 right-0 w-36 h-36 bg-white/10 rounded-full blur-2xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-28 h-28 bg-purple-400/20 rounded-full blur-xl pointer-events-none" />

            <div className="relative z-10 flex items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="inline-flex items-center gap-1 bg-white/15 backdrop-blur-md px-2.5 py-0.5 rounded-full text-[9px] font-black tracking-widest uppercase border border-white/20">
                  <Sparkles size={11} className="text-amber-300" /> {totalSections} Mini Retos
                </div>
                <h1 className="text-xl font-black tracking-tight leading-tight pt-0.5">
                  {challenge?.title}
                </h1>
                <p className="text-xs text-indigo-100/90 font-medium leading-relaxed">
                  {challenge?.description}
                </p>
              </div>

              <div className="w-14 h-14 shrink-0 flex items-center justify-center scale-95">
                <Mascot type={activeSkin} />
              </div>
            </div>

            {/* Barra de progreso */}
            <div className="relative z-10 pt-1 space-y-1.5">
              <div className="flex justify-between items-center text-xs font-black text-indigo-100">
                <span>Progreso</span>
                <span>{completedCount}/{totalSections} completados ({progressPct}%)</span>
              </div>

              <div className="h-2.5 w-full bg-black/25 rounded-full overflow-hidden p-0.5 border border-white/20">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-cyan-300 rounded-full transition-all duration-700 shadow-sm"
                  style={{ width: `${progressPct}%` }}
                />
              </div>

              <div className="flex items-center justify-between text-[10px] text-indigo-200 font-semibold pt-0.5">
                <span className="flex items-center gap-1">
                  <Clock size={11} /> ~{challenge?.estimatedMinutes || 12} min en total
                </span>
                {isAllCompleted && (
                  <span className="text-emerald-300 font-black flex items-center gap-1">
                    <CheckCircle2 size={12} /> ¡Todo listo!
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── CTA SI ESTÁ COMPLETADO ── */}
          {isAllCompleted && (
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-4 text-white shadow-xl flex items-center justify-between gap-3 animate-in zoom-in duration-300 border-b-4 border-emerald-800">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-xl shadow-inner shrink-0">
                  🏆
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-wider text-emerald-100">
                    ¡Misión cumplida!
                  </p>
                  <p className="text-xs font-black text-white">
                    Completaste todos los mini retos
                  </p>
                </div>
              </div>

              <button
                onClick={onCompleteChallenge || (() => setView('weekly-challenge-complete'))}
                className="px-3.5 py-2.5 bg-white text-emerald-800 font-black text-xs rounded-xl shadow-md hover:bg-emerald-50 active:scale-95 transition-all shrink-0 flex items-center gap-1 border-b-2 border-emerald-200 cursor-pointer"
              >
                Ver Cierre <ChevronRight size={14} />
              </button>
            </div>
          )}

          {/* ── LISTA DE SECCIONES / MINI RETOS ── */}
          <div className="space-y-2.5">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                Etapas del desafío
              </h2>
              {completedCount > 0 && (
                <button
                  onClick={handleRestartChallenge}
                  className="text-[10px] font-bold text-slate-400 hover:text-indigo-600 transition-colors flex items-center gap-1 cursor-pointer"
                >
                  <RotateCcw size={11} /> Reiniciar
                </button>
              )}
            </div>

            {sections.map((section, idx) => {
              const sectionInfo = progressData.sections?.[section.id] || {};
              const status = sectionInfo.status || 'not_started';
              const isCompleted = status === 'completed';
              const isInProgress = status === 'in_progress';
              const isUnlocked = isSectionUnlocked(idx);

              return (
                <div
                  key={section.id}
                  className={`rounded-2xl p-3.5 border-2 transition-all duration-300 relative overflow-hidden
                    ${isCompleted
                      ? 'bg-white border-emerald-200 shadow-sm'
                      : isInProgress
                      ? 'bg-white border-indigo-300 shadow-md ring-2 ring-indigo-100'
                      : isUnlocked
                      ? 'bg-white border-slate-200 shadow-sm hover:border-indigo-200'
                      : 'bg-slate-50 border-slate-200/80 opacity-60'
                    }`}
                >
                  {/* Barra lateral de estado */}
                  <div
                    className={`absolute left-0 top-0 bottom-0 w-1.5
                      ${isCompleted ? 'bg-emerald-500' : isInProgress ? 'bg-indigo-500' : isUnlocked ? 'bg-slate-300' : 'bg-slate-200'}`}
                  />

                  <div className="flex items-center gap-3 pl-1.5">
                    {/* Icono de la sección */}
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0 border-2
                        ${isCompleted
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                          : isInProgress
                          ? 'bg-indigo-50 border-indigo-200'
                          : isUnlocked
                          ? 'bg-slate-50 border-slate-200'
                          : 'bg-slate-200 border-slate-300 text-slate-400'
                        }`}
                    >
                      {isUnlocked ? section.icon || '📝' : <Lock size={16} />}
                    </div>

                    {/* Títulos y metadata */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                          Parte {idx + 1}
                        </span>
                        {isCompleted && (
                          <span className="bg-emerald-100 text-emerald-700 text-[9px] font-black px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                            <CheckCircle2 size={9} /> {sectionInfo.score}/{sectionInfo.total || section.totalQuestions} ({sectionInfo.accuracy}%)
                          </span>
                        )}
                        {isInProgress && (
                          <span className="bg-amber-100 text-amber-700 text-[9px] font-black px-1.5 py-0.5 rounded-full">
                            En curso
                          </span>
                        )}
                      </div>

                      <h3 className="font-black text-sm text-slate-800 truncate mt-0.5">
                        {section.title}
                      </h3>
                      <p className="text-[11px] text-slate-400 font-medium truncate">
                        {section.subtitle}
                      </p>
                      <p className="text-[10px] text-slate-400 font-semibold mt-0.5 flex items-center gap-1.5">
                        <span>⏱ {section.estimatedMinutes} min</span>
                        <span>·</span>
                        <span>{section.totalQuestions || section.set?.questions?.length || 0} preguntas</span>
                      </p>
                    </div>

                    {/* Botón de acción */}
                    <div className="shrink-0">
                      {isUnlocked ? (
                        <button
                          onClick={() => onStartSection(section)}
                          className={`px-3 py-2 rounded-xl font-black text-xs transition-all flex items-center gap-1 shadow-sm active:scale-95 border-b-2 cursor-pointer
                            ${isCompleted
                              ? 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200'
                              : isInProgress
                              ? 'bg-indigo-600 text-white border-indigo-900 hover:bg-indigo-700 shadow-indigo-200'
                              : 'bg-indigo-600 text-white border-indigo-900 hover:bg-indigo-700 shadow-indigo-200'
                            }`}
                        >
                          {isCompleted ? (
                            <>Repasar</>
                          ) : isInProgress ? (
                            <>Continuar <Play size={10} fill="currentColor" /></>
                          ) : (
                            <>Iniciar <Play size={10} fill="currentColor" /></>
                          )}
                        </button>
                      ) : (
                        <div className="p-2.5 bg-slate-100 rounded-xl text-slate-400">
                          <Lock size={14} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </main>
      </div>
    </div>
  );
}
